// panel-discovery.js: at boot, subscribe to `pg://entities?type=iam:panel`, dereference each manifest,
// verify its Authority signature against the JWKS, and return validated panel-registry entries.
// Manifests are substrate-served and never CDN-cached so signatures stay fresh. The bundle is CDN-served,
// pinned by bundle_hash, and fetched lazily on activation via fetchAndVerifyBundle.
// The JWKS is fetched once at boot and cached.

import {
    parsePanelManifest,
    verifyManifest,
    fetchAndVerifyBundle,
} from './render-host/panel-manifest-verify.js';
import { getTracer } from './render-host/otel.js';

const PANEL_PREDICATE = 'pg://entities?type=iam:panel';
export const PANEL_PREDICATE_URI = PANEL_PREDICATE;

// Fail-soft tracer wrappers: tracer errors never break discovery.
function _startDiscoverySpan(name, attrs) {
    const tracer = getTracer();
    if (!tracer || typeof tracer.startSpan !== 'function') return null;
    try {
        return tracer.startSpan(name, { attributes: attrs });
    } catch (_) {
        return null;
    }
}
function _endDiscoverySpan(span, attrs) {
    if (!span) return;
    try {
        if (attrs) {
            for (const k of Object.keys(attrs)) {
                span.setAttribute(k, attrs[k]);
            }
        }
        span.end();
    } catch (_) { /* tracer errors don't break discovery */ }
}

// The v1 Authority's panel-manifest key (kid mesh-authority-v1), served by the wt-server edge.
// It is not authority.coglative.com, which holds the v0 cap-token key. The URL is same-origin so the chrome
// fetches its own origin's JWKS; the bytes are identical per host.
const JWKS_URL = '/.well-known/jwks.json';

// Minimal parsers for PredicateResult and EntityContent, since bridge-op.js does not export its own.

// parsePredicateResultUris — repeated entity_uris (field 2) out of a cache_line.PredicateResult.
export function parsePredicateResultUris(bytes) {
    if (!(bytes instanceof Uint8Array) || bytes.byteLength === 0) return [];
    const decoder = new TextDecoder('utf-8');
    const uris = [];
    let i = 0;
    while (i < bytes.length) {
        let tag = 0, shift = 0;
        while (i < bytes.length) {
            const b = bytes[i++];
            tag |= (b & 0x7f) << shift;
            if ((b & 0x80) === 0) break;
            shift += 7;
        }
        const fNum = tag >>> 3;
        const wType = tag & 0x07;
        if (wType === 2) {
            let len = 0, lShift = 0;
            while (i < bytes.length) {
                const b = bytes[i++];
                len |= (b & 0x7f) << lShift;
                if ((b & 0x80) === 0) break;
                lShift += 7;
            }
            if (fNum === 2) {
                uris.push(decoder.decode(bytes.slice(i, i + len)));
            }
            i += len;
        } else if (wType === 0) {
            while (i < bytes.length && (bytes[i++] & 0x80) !== 0) {}
        } else if (wType === 1) {
            i += 8;
        } else if (wType === 5) {
            i += 4;
        } else {
            break;
        }
    }
    return uris;
}

// parsePredicateResultContents: field 3 of a PredicateResult, the EntityContent bodies sent inline when
// Subscribe.want_contents is set. Returns Map<entity_uri, EntityContent bytes>.
export function parsePredicateResultContents(bytes) {
    const out = new Map();
    if (!(bytes instanceof Uint8Array) || bytes.byteLength === 0) return out;
    const decoder = new TextDecoder('utf-8');
    const visit = function (buf, onField) {
        let i = 0;
        while (i < buf.length) {
            let tag = 0, shift = 0;
            while (i < buf.length) {
                const b = buf[i++];
                tag |= (b & 0x7f) << shift;
                if ((b & 0x80) === 0) break;
                shift += 7;
            }
            const f = tag >>> 3, w = tag & 0x07;
            if (w === 2) {
                let len = 0, ls = 0;
                while (i < buf.length) {
                    const b = buf[i++];
                    len |= (b & 0x7f) << ls;
                    if ((b & 0x80) === 0) break;
                    ls += 7;
                }
                onField(f, buf.slice(i, i + len));
                i += len;
            } else if (w === 0) { while (i < buf.length && (buf[i++] & 0x80) !== 0) {} }
            else if (w === 1) i += 8;
            else if (w === 5) i += 4;
            else break;
        }
    };
    visit(bytes, function (f, body) {
        if (f !== 3) return;
        let uri = '';
        visit(body, function (ef, ebody) { if (ef === 1) uri = decoder.decode(ebody); });
        if (uri) out.set(uri, body);
    });
    return out;
}

// parseEntityContentPayload: field 3 (bytes payload) of an EntityContent, or empty when absent.
export function parseEntityContentPayload(bytes) {
    if (!(bytes instanceof Uint8Array) || bytes.byteLength === 0) {
        return new Uint8Array(0);
    }
    let i = 0;
    while (i < bytes.length) {
        let tag = 0, shift = 0;
        while (i < bytes.length) {
            const b = bytes[i++];
            tag |= (b & 0x7f) << shift;
            if ((b & 0x80) === 0) break;
            shift += 7;
        }
        const fNum = tag >>> 3;
        const wType = tag & 0x07;
        if (wType === 2) {
            let len = 0, lShift = 0;
            while (i < bytes.length) {
                const b = bytes[i++];
                len |= (b & 0x7f) << lShift;
                if ((b & 0x80) === 0) break;
                lShift += 7;
            }
            if (fNum === 3) {
                return bytes.slice(i, i + len);
            }
            i += len;
        } else if (wType === 0) {
            while (i < bytes.length && (bytes[i++] & 0x80) !== 0) {}
        } else if (wType === 1) {
            i += 8;
        } else if (wType === 5) {
            i += 4;
        } else {
            break;
        }
    }
    return new Uint8Array(0);
}


let jwksCache = null;

export async function fetchJWKS(url = JWKS_URL) {
    if (jwksCache) {
        const span = _startDiscoverySpan('panel-discovery.fetchJWKS', {
            'jwks.url': url,
        });
        _endDiscoverySpan(span, {
            'discovery.outcome': 'cached',
            'key.count': Array.isArray(jwksCache.keys) ? jwksCache.keys.length : 0,
        });
        return jwksCache;
    }
    const span = _startDiscoverySpan('panel-discovery.fetchJWKS', {
        'jwks.url': url,
    });
    try {
        // The JWKS is public; the Bearer lets the shell count requests per principal.
        const fetchFn = (window.OAuthClient && window.OAuthClient.authedFetch) || fetch;
        const resp = await fetchFn(url);
        if (!resp.ok) {
            // eslint-disable-next-line no-console
            console.warn('[panel-discovery] JWKS fetch returned HTTP', resp.status);
            _endDiscoverySpan(span, {
                'discovery.outcome': 'http_error',
                'http.status_code': resp.status,
                'key.count': 0,
            });
            return null;
        }
        jwksCache = await resp.json();
        _endDiscoverySpan(span, {
            'discovery.outcome': 'success',
            'key.count': Array.isArray(jwksCache.keys) ? jwksCache.keys.length : 0,
        });
        return jwksCache;
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[panel-discovery] JWKS fetch failed:',
            e && e.message ? e.message : e);
        _endDiscoverySpan(span, {
            'discovery.outcome': 'network_error',
            'key.count': 0,
        });
        return null;
    }
}

// resetJwksCache — test-only; clears the module-local cache.
export function resetJwksCache() {
    jwksCache = null;
}


// _entryFromFields builds the registry entry for a verified manifest, shared by the inline and fetch paths.
function _entryFromFields(fields, options) {
    return {
                id: fields.panel_id,
                title: fields.display_name || fields.panel_id,
                // icon_id is signed over; the fallback covers early v0 manifests without one.
                icon: fields.icon_id || 'entities',
                kind: 'wasm',
                bundle_hash: fields.bundle_hash,
                // load fetches the bundle on activation and verifies its SHA-256 against bundle_hash.
                // Without `bundle_repo` it falls back to the v0 repo convention.
                load: function () {
                    const repo = fields.bundle_repo || ('wasm-public/' + fields.panel_id);
                    return fetchAndVerifyBundle(repo, fields.bundle_hash, options.bundleBaseUrl);
                },
                // The boot-override path replaces the static registry entry wholesale, so frame
                // hints must come from the manifest.
                containerClass: fields.container_class || undefined,
                bridgePredicate: fields.bridge_predicate || undefined,
    };
}

// discoverPanels returns validated registry records, or [] on any boot failure, since the panel-host is connected-only.
// A refused panel subscribe is retried briefly, then thrown with err.refused set: it is not an empty registry.
export async function discoverPanels(opts) {
    const options = opts || {};
    const bridgeOp = options.bridgeOp || (typeof window !== 'undefined' && window.BridgeOp);
    if (!bridgeOp
        || typeof bridgeOp.subscribe !== 'function'
        || typeof bridgeOp.getEntity !== 'function') {
        return [];
    }

    // Performance marks, because spans here go to the no-op provider before getDefaultRenderHost()
    // installs an exporter.
    const _dm = function (name, from) {
        try {
            const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
            window.__discoveryPhases = window.__discoveryPhases || {};
            window.__discoveryPhases[name] = Math.round(now - from);
        } catch (_) {}
        return (typeof performance !== 'undefined' ? performance.now() : Date.now());
    };
    let _dt = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const jwks = options.jwks || await fetchJWKS(options.jwksUrl);
    _dt = _dm('jwks', _dt);
    if (!jwks) return [];

    const subscribeSpan = _startDiscoverySpan('panel-discovery.subscribe', {
        'predicate.uri': PANEL_PREDICATE,
    });
    const subscribeFn = typeof bridgeOp.subscribeOrRefuse === 'function'
        ? bridgeOp.subscribeOrRefuse : bridgeOp.subscribe;
    const refusedRetries = typeof options.refusedRetries === 'number' ? options.refusedRetries : 2;
    let predicateBytes;
    for (let attempt = 0; ; attempt++) {
        try {
            predicateBytes = await subscribeFn(PANEL_PREDICATE);
            _dt = _dm('subscribe', _dt);
            break;
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('[panel-discovery] subscribe failed:',
                e && e.message ? e.message : e);
            if (e && e.refused && attempt < refusedRetries) {
                await new Promise(function (r) { setTimeout(r, 500 * Math.pow(2, attempt)); });
                continue;
            }
            _endDiscoverySpan(subscribeSpan, {
                'discovery.outcome': 'subscribe_rejected',
                'uri.count': 0,
            });
            if (e && e.refused) throw e;
            return [];
        }
    }

    const uris = parsePredicateResultUris(
        predicateBytes instanceof Uint8Array ? predicateBytes : new Uint8Array(0)
    );
    _endDiscoverySpan(subscribeSpan, {
        'discovery.outcome': uris.length > 0 ? 'success' : 'empty_result',
        'uri.count': uris.length,
    });
    if (uris.length === 0) return [];

    const registered = [];
    // Manifests resolve concurrently. Mapping rather than pushing preserves order, which drives tab order.
    // A uri whose body arrived inline costs no round trip.
    const inlineContents = parsePredicateResultContents(predicateBytes);
    const settled = await Promise.all(uris.map(async function (uri) {
        const getEntitySpan = _startDiscoverySpan('panel-discovery.getEntity', {
            'entity.uri': uri,
        });
        const inline = inlineContents.get(uri);
        if (inline) {
            _endDiscoverySpan(getEntitySpan, {
                'discovery.outcome': 'inline', 'byte.count': inline.byteLength,
            });
            const inlineManifest = parseEntityContentPayload(inline);
            if (inlineManifest.byteLength === 0) return null;
            const inlineResult = await verifyManifest(inlineManifest, jwks);
            if (!inlineResult.valid) {
                // eslint-disable-next-line no-console
                console.warn('[panel-discovery] inline manifest verify failed for', uri, ':',
                    inlineResult.error);
                return null;
            }
            return _entryFromFields(inlineResult.fields, options);
        }
        let entityBytes;
        try {
            entityBytes = await bridgeOp.getEntity(uri);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('[panel-discovery] getEntity', uri, 'failed:',
                e && e.message ? e.message : e);
            _endDiscoverySpan(getEntitySpan, {
                'discovery.outcome': 'failed',
                'byte.count': 0,
            });
            return null;
        }
        if (!(entityBytes instanceof Uint8Array) || entityBytes.byteLength === 0) {
            _endDiscoverySpan(getEntitySpan, {
                'discovery.outcome': 'empty',
                'byte.count': 0,
            });
            return null;
        }
        _endDiscoverySpan(getEntitySpan, {
            'discovery.outcome': 'success',
            'byte.count': entityBytes.byteLength,
        });

        const manifestBytes = parseEntityContentPayload(entityBytes);
        if (manifestBytes.byteLength === 0) return null;

        const result = await verifyManifest(manifestBytes, jwks);
        if (!result.valid) {
            // eslint-disable-next-line no-console
            console.warn('[panel-discovery] manifest verify failed for', uri, ':',
                result.error);
            return null;
        }

        const fields = result.fields;
        return _entryFromFields(fields, options);
    }));
    _dt = _dm('manifests', _dt);
    for (const entry of settled) { if (entry) registered.push(entry); }

    return registered;
}

if (typeof window !== 'undefined') {
    window.PanelDiscovery = {
        discoverPanels: discoverPanels,
        fetchJWKS: fetchJWKS,
        resetJwksCache: resetJwksCache,
        // Test-only proto helpers (mirrors the BridgeOpInternals pattern).
        parsePredicateResultUris: parsePredicateResultUris,
        parseEntityContentPayload: parseEntityContentPayload,
    };
}
